﻿<script setup lang="ts">
import { computed } from 'vue'
import type { CategoryRecord, Id } from '../types/catalog'

const props = defineProps<{
  categories: CategoryRecord[]
  modelValue: Id | null
  required?: boolean
  label?: string
  emptyLabel?: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: Id | null] }>()

const flattened = computed(() => {
  const byParent = new Map<Id | null, CategoryRecord[]>()
  for (const category of props.categories) {
    const siblings = byParent.get(category.parentId) ?? []
    siblings.push(category)
    byParent.set(category.parentId, siblings)
  }
  for (const siblings of byParent.values()) siblings.sort((a, b) => a.name.localeCompare(b.name, 'en'))

  const result: CategoryRecord[] = []
  const visit = (parentId: Id | null) => {
    for (const category of byParent.get(parentId) ?? []) {
      result.push(category)
      visit(category.id)
    }
  }
  visit(null)
  return result
})
</script>

<template>
  <label class="field">
    <span>{{ label ?? 'Category' }}</span>
    <select
      :value="modelValue ?? ''"
      :required="required"
      @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value || null)"
    >
      <option v-if="!required" value="">{{ emptyLabel ?? 'All categories' }}</option>
      <option v-for="category in flattened" :key="category.id" :value="category.id">
        {{ `${'— '.repeat(category.depth)}${category.name}` }}
      </option>
    </select>
  </label>
</template>
